import { useMemo, useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip } from '@c2fo/liquidity';
import { TechnicalDifficultyIcon, XmarkIcon } from '@c2fo/liquidity/icons';
import NoDataState from '@/enterprise/components/NoDataState';
import { Table, TableBody, TableDisplaySkeletonRows } from '@/enterprise/components/Table';
import useSubstitutionRecords, {
  getSubstitutionCustomers,
  SubstitutionCustomer,
} from '@/enterprise/data/useSubstitutionRecords';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useLocaleFormat from '@/utils/useLocaleFormat';
import useUrlState from '@/utils/useUrlState';
import SubstitutionRecordsFilter from './SubstitutionRecordsFilter';
import {
  countAppliedFilters,
  filterSubstitutionRecords,
  parseSubstitutionRecordsFilters,
  SubstitutionRecordsFilters,
  SubstitutionRecordsUrlState,
} from './substitutionRecordsFilters';
import SubstitutionRecordsTable, { COLUMN_COUNT, SubstitutionRecordsTableHeader } from './SubstitutionRecordsTable';

interface SubstitutionRecordsResultsProps {
  customers: SubstitutionCustomer[];
  filters: SubstitutionRecordsFilters;
  /** True while a filter change loads; the current rows stay on screen, blurred. */
  loading: boolean;
  hasFiltersApplied: boolean;
  onAdjustFilters: () => void;
}

const SubstitutionRecordsResults = ({
  customers,
  filters,
  loading,
  hasFiltersApplied,
  onAdjustFilters,
}: SubstitutionRecordsResultsProps) => {
  const records = useSubstitutionRecords({ customers, startDate: filters.startDate, endDate: filters.endDate });

  // Platform's stats route isn't paginated, so every matching record is already loaded; show them all.
  const filteredRecords = filterSubstitutionRecords(records, { awardDate: filters.awardDate });

  return (
    <SubstitutionRecordsTable
      records={filteredRecords}
      loading={loading}
      hasFiltersApplied={hasFiltersApplied}
      onAdjustFilters={onAdjustFilters}
    />
  );
};

const SubstitutionRecordsView = () => {
  const { t } = useTranslation();
  const { asDateStringText } = useLocaleFormat();
  const [filterOpen, setFilterOpen] = useState(false);
  const { data: takerMarkets } = useTakerMarkets();
  const [urlState, setUrlState] = useUrlState<Partial<SubstitutionRecordsUrlState>>({});
  // Reason: filter changes run as a transition, so once the first load is done React keeps the current
  // rows on screen (blurred) while new ones load, instead of falling back to the skeleton rows. Same as
  // Early Pay History.
  const [isPending, startTransition] = useTransition();

  const customers = useMemo(() => getSubstitutionCustomers(takerMarkets), [takerMarkets]);
  const filters = parseSubstitutionRecordsFilters(urlState);
  const appliedFilters = countAppliedFilters(filters);

  const clearFilters = () =>
    startTransition(() =>
      setUrlState({
        startDate: undefined,
        endDate: undefined,
        range: undefined,
        awardDate: undefined,
      })
    );

  return (
    // Reason: a wrapper, not a fragment, so a parent's space-y margin lands here and not on the
    // fixed-position drawer, which would otherwise stop short of the top of the viewport.
    <div>
      <SubstitutionRecordsFilter
        filterOpen={filterOpen}
        setFilterOpen={setFilterOpen}
        rangeKey={filters.rangeKey}
        startDate={filters.startDate}
        endDate={filters.endDate}
        onDateRangeChange={(selection) =>
          startTransition(() =>
            setUrlState(
              selection
                ? { startDate: selection.range.startDate, endDate: selection.range.endDate, range: selection.rangeKey }
                : { startDate: undefined, endDate: undefined, range: undefined }
            )
          )
        }
      />
      <div className="overflow-hidden rounded-xl bg-white shadow-md">
        <div className="flex flex-wrap items-center gap-2 bg-gray-100 px-4 py-6">
          <div className="relative">
            <Button onClick={() => setFilterOpen(true)}>{t('core.filters')}</Button>
            {appliedFilters > 0 && (
              <div
                className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-lightBlue-500 text-xs font-medium text-white"
                data-testid="applied-filters-count"
              >
                {appliedFilters}
              </div>
            )}
          </div>
          <Button onClick={clearFilters} variant="tertiary">
            {t('core.clearFilters')}
          </Button>
          {filters.awardDate && (
            <Chip
              endIcon={{ icon: XmarkIcon, name: t('instantPay.substitutions.removeAwardDate') }}
              isRound
              label={t('instantPay.substitutions.awardDate', { date: asDateStringText({ date: filters.awardDate }) })}
              onClick={() => setUrlState({ awardDate: undefined })}
              size="sm"
              type="secondary"
              variant="outlined"
            />
          )}
        </div>
        <QueryBoundaries
          LoadingComponent={() => (
            <Table>
              <SubstitutionRecordsTableHeader />
              <TableBody>
                <TableDisplaySkeletonRows colSpan={COLUMN_COUNT} rowCount={10} height="h-16" />
              </TableBody>
            </Table>
          )}
          ErrorComponent={() => (
            <div className="px-4 pb-6 pt-8">
              <NoDataState
                icon={<TechnicalDifficultyIcon className="size-24 pb-4" />}
                title={t('instantPay.substitutions.errorLoading')}
                message={t('instantPay.substitutions.errorLoadingDescription')}
                action={
                  <Button size="sm" variant="ancillary" onClick={() => window.location.reload()}>
                    {t('core.refresh')}
                  </Button>
                }
              />
            </div>
          )}
        >
          <SubstitutionRecordsResults
            customers={customers}
            filters={filters}
            loading={isPending}
            hasFiltersApplied={appliedFilters > 0}
            onAdjustFilters={() => setFilterOpen(true)}
          />
        </QueryBoundaries>
      </div>
    </div>
  );
};

export default SubstitutionRecordsView;
